import { useEffect } from "react";
import type { ApiClient, components } from "@cntrl/api-client";
import {
  endLiveActivity,
  isLiveActivityMuted,
  lastPushToken,
  liveActivitiesEnabled,
  onNativeResume,
  prepareLiveActivity,
  resolveApnsEnvironment,
  setLiveActivityMuted,
  startLiveActivityLocally,
} from "@cntrl/native";
import { API_URL, APP_ENV } from "../config/env.js";
import { buildLiveActivityPlan, liveActivityStaleAt, liveActivityStateAt } from "../features/mobile/dayOfShow.js";
import { useApp } from "./appContext.js";
import { useMe } from "./hooks/useSession.js";
import { useActingAs } from "./hooks/useDevFixtureSwitch.js";

type MyLiveActivityShow = components["schemas"]["MyLiveActivityShow"];
type LiveActivityPlan = components["schemas"]["LiveActivityPlan"];

/**
 * The DOS lock-screen Live Activity, AUTOMATIC (ART-208). Nobody presses anything: whenever the app is open (launch
 * and every return to the foreground) it asks the server which of the person's shows — across EVERY band — should
 * carry a card on this phone, computes each plan on the device (only the phone can turn a wall-clock run of show
 * into instants), and registers it. The server then push-starts the card at T-2h, moves it through every phase and
 * ends it after the final set, whether or not the app is open. A show already inside that window is also started
 * locally, so opening the app mid-show brings the card straight up.
 *
 * Each phone may MUTE a show ({@link muteLiveActivity}) — the DOS screen's bell. The mute lives on the phone, and it
 * also removes this phone's plan from the server, so no later push can bring the card back.
 *
 * Best-effort throughout: a failure is logged and retried on the next foreground, never surfaced — there is no
 * button for an error to be about.
 */

/** The plan last registered per show this session — an unchanged plan is not re-posted on every foreground. */
const registered = new Map<string, string>();
let inFlight: Promise<void> | null = null;

const sinkUrl = (): string | undefined => (API_URL ? `${API_URL}/api/v1/public/dos/live-activity/token` : undefined);

function planFor(show: MyLiveActivityShow): LiveActivityPlan | null {
  if (!show.showId || !show.bandId || !show.date) return null;
  return buildLiveActivityPlan({
    timezone: show.timezone,
    dateIso: show.date,
    items: show.runOfShow ?? [],
    ownSlotId: show.ownSlotId ?? null,
    attributes: {
      showId: show.showId,
      bandId: show.bandId,
      bandLabel: show.bandName || show.venueName || "Day of Show",
      venue: show.venueName ?? "",
      city: show.city ?? "",
    },
  });
}

async function register(
  api: ApiClient,
  show: MyLiveActivityShow,
  plan: LiveActivityPlan,
  appEnv: string,
  nowMs: number,
): Promise<void> {
  const showId = show.showId!;
  const bandId = show.bandId!;
  const prepared = await prepareLiveActivity({ showId, sinkUrl: sinkUrl(), appEnv });
  if (!prepared) return;

  const post = async (activityToken?: string) => {
    const { response } = await api.POST("/api/v1/bands/{bandId}/shows/{showId}/dos/live-activity", {
      params: { path: { bandId, showId } },
      body: {
        plan,
        deviceKey: prepared.deviceKey,
        startToken: prepared.startToken ?? undefined,
        activityToken,
        appEnv,
        sinkKey: prepared.sinkKey ?? undefined,
        // The day-of-show alerts skip a phone already showing the card (ART-259 D3).
        pushToken: lastPushToken() ?? undefined,
      },
    });
    if (!response.ok) throw new Error(`register ${showId}: HTTP ${response.status}`);
  };

  // Plan FIRST: a card started below reports its update token through the token sink, which only resolves once
  // the plan exists.
  const json = JSON.stringify(plan);
  if (registered.get(showId) !== json) {
    await post();
    registered.set(showId, json);
  }

  // Inside the card's window (T-2h → the end of the final set): put it on the lock screen now, at the phase that is
  // on. Re-running this on every foreground is harmless — the native side updates the card already there.
  if (nowMs >= (plan.startEpochMs ?? Infinity) && nowMs < (plan.endEpochMs ?? 0)) {
    const activityToken = await startLiveActivityLocally({
      attributesType: plan.attributesType ?? "DosLiveActivityAttributes",
      attributes: plan.attributes,
      contentState: liveActivityStateAt(plan, nowMs),
      staleDateEpochSec: Math.round(liveActivityStaleAt(plan, nowMs) / 1000),
      sinkUrl: sinkUrl(),
      appEnv,
    });
    if (activityToken) await post(activityToken);
  }
}

/** Register (and, in its window, start) the card for every eligible show — or just `onlyShowId`. Resolves to the
 *  shows whose registration FAILED (an unreachable list counts against `onlyShowId`), so the one caller that has a
 *  person waiting on it — unmute — can say so. */
export function syncLiveActivities(api: ApiClient, onlyShowId?: string): Promise<string[]> {
  if (inFlight) return inFlight.then(() => syncLiveActivities(api, onlyShowId));
  const failed: string[] = [];
  const run: Promise<string[]> = (async () => {
    try {
      if (!(await liveActivitiesEnabled())) return failed;
      const { data, response } = await api.GET("/api/v1/me/dos/live-activity/shows");
      if (!response.ok || !data) {
        console.warn(`[live-activity] eligible shows: HTTP ${response.status}`);
        if (onlyShowId) failed.push(onlyShowId);
        return failed;
      }
      const appEnv = await resolveApnsEnvironment(APP_ENV);
      const nowMs = Date.now();
      for (const show of data) {
        if (!show.showId || (onlyShowId && show.showId !== onlyShowId)) continue;
        try {
          if (await isLiveActivityMuted(show.showId)) continue;
          const plan = planFor(show);
          // No timed set, or the final set has already ended: nothing to show — and a card still on this phone for
          // it is ended here. The server's end push normally does that, but when it is late or lost the card would
          // otherwise sit on the lock screen until iOS retires it hours later.
          if (!plan || (plan.endEpochMs ?? 0) <= nowMs) {
            await endLiveActivity(show.showId);
            continue;
          }
          await register(api, show, plan, appEnv, nowMs);
        } catch (err) {
          console.warn("[live-activity] show", show.showId, err instanceof Error ? err.message : err);
          failed.push(show.showId);
        }
      }
    } catch (err) {
      console.warn("[live-activity] sync failed:", err instanceof Error ? err.message : err);
      if (onlyShowId) failed.push(onlyShowId);
    } finally {
      inFlight = null;
    }
    return failed;
  })();
  inFlight = run.then(() => undefined);
  return run;
}

/** Mute the show's card on THIS phone: remember it, end the card, and remove this phone's plan from the server.
 *  THROWS when the server could not be told — the mute is then undone (left in place, the server would keep pushing
 *  the card back), so the caller can roll its control back and say so. */
export async function muteLiveActivity(api: ApiClient, bandId: string, showId: string): Promise<void> {
  await setLiveActivityMuted(showId, true);
  registered.delete(showId);
  await endLiveActivity(showId);
  const prepared = await prepareLiveActivity({ showId });
  if (!prepared) return;
  let ok = false;
  try {
    const { response } = await api.DELETE("/api/v1/bands/{bandId}/shows/{showId}/dos/live-activity", {
      params: { path: { bandId, showId }, query: { deviceKey: prepared.deviceKey } },
    });
    ok = response.ok;
  } catch {
    ok = false;
  }
  if (!ok) {
    await setLiveActivityMuted(showId, false);
    void syncLiveActivities(api, showId);   // the card is still the server's to push — put it back here too
    throw new Error("Couldn't mute the Live Activity");
  }
}

/** Unmute: forget the mute and set the card up again (it appears at once if the show is already in its window).
 *  THROWS when the card could not be registered — the mute is restored, so the caller can roll back and say so. */
export async function unmuteLiveActivity(api: ApiClient, showId: string): Promise<void> {
  await setLiveActivityMuted(showId, false);
  registered.delete(showId);
  const failed = await syncLiveActivities(api, showId);
  if (failed.includes(showId)) {
    await setLiveActivityMuted(showId, true);
    throw new Error("Couldn't unmute the Live Activity");
  }
}

/**
 * Run the sync once per signed-in person and again on every return to the foreground. Mounted from each authed
 * shell layout, beside {@link usePushRegistration}. Never while the DEV "View as" switcher is wearing someone else —
 * the phone would register for the fixture person's shows and keep their cards after the swap ends.
 */
export function useLiveActivitySync(): void {
  const { api } = useApp();
  const personId = useMe().data?.person?.personId;
  const actingAs = useActingAs();

  useEffect(() => {
    if (!personId || actingAs) return;
    void syncLiveActivities(api);
    return onNativeResume(() => void syncLiveActivities(api));
  }, [api, personId, actingAs]);
}

/** Tests only. */
export function _resetLiveActivitySyncForTest(): void {
  registered.clear();
  inFlight = null;
}
